import { AxiosResponse } from 'axios';

import { getBaseUrlConfig } from '@core/base-url';
import { $httpCommonApi, $httpRootApi, getCancelRequestSource } from '@core/http-client';
import { getQueriesAsSearch } from '@shared/utils/common';

export interface DownloadFileResponse {
  created_at: string;
  created_by: number;
  file_name: string | null;
  id: number;
  notify: number;
  object_id: number;
  organisation_id: number;
  path: string | null;
  ready_at: string | null;
  status: number;
  type: number;
  updated_at: string;
}

export const downloadFile = (url: string) => {
  // Download now
  const a = document.createElement('a');
  a.href = url;
  a.download = 'download';
  a.click();
  return url;
};

export const parseBytes = (bytes: number, decimals = 2): { amount: number; unit: string } => {
  const units = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  if (bytes === 0) {
    return {
      amount: 0,
      unit: units[0] as string,
    };
  }

  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return {
    amount: parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)),
    unit: units[i] as string,
  };
};

export const createFormDataName = (file: File, name: string) => {
  const formData = new FormData();

  formData.append(name, file);

  return formData;
};

export const getBase64FromFile = (file: File): Promise<string> => {
  const reader = new FileReader();

  return new Promise((resolve) => {
    reader.onload = ({ target }) => {
      if (target) {
        resolve(String(target.result));
      }
    };

    reader.readAsDataURL(file);
  });
};

export const downloadingFileWithTimeout = (params?: { report_id: number; report_type: number }) => {
  let isCanceled = false;
  const source = getCancelRequestSource();

  const download = async () => {
    while (!isCanceled && params) {
      const { data } = await $httpCommonApi.get<any>(`/downloads/${params.report_type}/${params.report_id}`, {
        cancelToken: source.token,
      });

      switch (data.status) {
        // Status 0 is WAITING
        case 0: {
          await new Promise((resolve) => setTimeout(resolve, 7000));
          break;
        }

        // Status 1 is READY
        case 1: {
          const url = `${getBaseUrlConfig().commonRoot}/downloads/${data.id}`;

          downloadFile(url);

          return url;
        }

        // Status 2 is ERROR
        case 2:
        default: {
          throw new Error();
        }
      }
    }
  };

  return {
    promise: download(),
    cancel: () => {
      isCanceled = true;
      source.cancel();
    },
  };
};

export interface PollExportResponse {
  report_id: number;
  report_type: number;
}

type PollFileMethod = 'GET' | 'POST';

export const pollExportFile = <T = any>(params: T, url: string, method?: PollFileMethod) => {
  const source = getCancelRequestSource();
  const requestMethod = method || 'GET';

  const download = async () => {
    if (requestMethod === 'POST') {
      return await $httpRootApi.post<T, AxiosResponse<PollExportResponse>>(url, params, {
        cancelToken: source.token,
      });
    }

    return await $httpRootApi.get<T, AxiosResponse<PollExportResponse>>(`${url}${getQueriesAsSearch(params)}`, {
      cancelToken: source.token,
    });
  };

  return {
    pollPromise: download(),
    cancelPoll: () => {
      source.cancel();
    },
  };
};
